// ============================================================
// useTurnTracker - Main turn tracker orchestration hook.
//
// Composes engine pure functions with React state, Firestore
// sync, countdown integration, and undo system awareness.
//
// KEY DESIGN: endTurn consolidates ALL target mutations into a
// single setTabs call to prevent multiple Firestore syncs,
// echo timing issues, and double undo entries.
// ============================================================

import type { StoredInitiative } from '../engine/roster/hidden';
import { useCallback, useEffect, useMemo } from 'react';
import useStableCallback from './useStableCallback';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import {
  applyInitiative as applyInitiativePure,
  dismissJoinHint as dismissJoinHintPure,
  removeParticipant as removeParticipantPure,
  replaceParticipant as replaceParticipantPure,
  reorderParticipants as reorderParticipantsPure,
  setInitiative as setInitiativePure,
  setOrderMode as setOrderModePure,
  deactivateTracker as deactivateTrackerPure,
  resetRound as resetRoundPure,
  isActiveParticipant as isActiveParticipantPure,
  isTrackedParticipant as isTrackedParticipantPure,
} from '../engine/turnTracker';
import { DEFAULT_INITIATIVE_ARRIVAL, DEFAULT_INITIATIVE_TIE_BREAK } from '../engine/initiative';
import type { InitiativeArrival, InitiativeTieBreak } from '../engine/initiative';
import { isDowned } from '../engine/conditions';
import { isObject } from '../engine/bodyShape';
import type { Character } from '../engine/character/types';
import { DEFAULT_UNNATURAL_DOS_MODE, type UnnaturalDosMode } from '../engine/unnaturalDos';
import { rollAssignments } from './turnTrackerRolls';
import { useAdmission } from './useTurnTrackerAdmission';
import { clearTickFlags, resolveTurnEffectTestWith, runEndTurn, runTimePasses } from './turnTrackerTurn';
import type { TurnEffectTestRequest, TurnEndContext } from './turnTrackerTurn';
import type { TurnTrackerState, TurnParticipant, TurnOrderMode, Tab, NarrativeEntry } from '../types';

// ── Params & Return types ────────────────────────────────────

/**
 * One arrival at the turn order.
 *
 * `sheet` is passed IN rather than looked up, because the caller may be
 * admitting a combatant it created moments ago in the same tick - the
 * roster is a scheduled state update and will not have it yet. Omitted,
 * `sheetFor` is consulted as a fallback; null either way is a bare d10,
 * which is right for a custom entry and wrong for anybody else.
 */
export interface TurnAdmission {
  combatantId: string;
  name: string;
  sheet?: Character | null;
}

export interface AdmitEntryOptions {
  name?: string;
  sheet?: Character | null;
  /**
   * Is this its own undo step?
   *
   * True for a deliberate "Track Participant"; false (the default) when
   * admission is a side effect of creating the combatant - see `admit`.
   */
  undoable?: boolean;
  /** The initiative to come back with - a revealed figure keeps the one it was hidden with; no die is rolled. */
  initiative?: StoredInitiative | null;
}

export interface UseTurnTrackerParams {
  tabs: Tab[];
  isLocked: (feature: string) => boolean;
  turnTrackerState: TurnTrackerState;
  setTurnTrackerState: Dispatch<SetStateAction<TurnTrackerState>>;
  /** Send an EDIT - what we started from and what we made of it - to be merged on the server (v8.310.6). */
  syncTurnTrackerChange: (base: TurnTrackerState, next: TurnTrackerState) => void;
  /**
   * REPLACE the document whole. The named exception to the edit path,
   * for a state this client did not derive from the server's: undo, and
   * a scene cut (v8.337.0). Optional: a bare hook has no server.
   */
  syncTurnTrackerToFirestore?: (state: TurnTrackerState) => void;
  // From useHistory
  capturePreActionSnapshot: () => void;
  historyTrack: MutableRefObject<boolean>;
  // Helpers from parent
  updateTab: (combatantId: string, updater: (tab: Tab) => Tab) => void;
  addNarrative: (combatantId: string, entry: NarrativeEntry) => void;
  /** GM house rule: roll the extinguish test automatically. Default false. */
  autoResolveTurnEffects?: boolean;
  /** GM house rule: timed conditions count down each round. Default false. */
  roundCountdown?: boolean;
  /** The table's protagonist ruling. Absent means off. */
  protagonistRules?: boolean;
  /** Unnatural-DoS house rule, mirrored into the test roll. */
  unnaturalDosMode?: UnnaturalDosMode;
  /** Surface pending tests to the UI. Omitted = no prompt (tests roll or are skipped). */
  onTurnEffectTests?: (requests: TurnEffectTestRequest[]) => void;
  /** Each turn start, by combatant (Squad Mode's turn-start rules; see turnTrackerTurn). */
  onTurnStart?: (combatantId: string) => void;

  // ---- Initiative (v8.72.0) ----
  /**
   * The sheet behind a combatant, for its Agility Bonus and talents.
   *
   * A function rather than the roster itself: this hook has no business
   * knowing the roster's shape, and it needs exactly one thing from it.
   * Omitted (or returning null) means a bare d10, which is also what a
   * custom participant gets.
   */
  sheetFor?: (combatantId: string) => Character | null | undefined;
  /** This is the GM's screen: the one that repairs the order (an Object in it, 2026-10-26), so one client writes. */
  isGm?: boolean;
  /** What the table adds to a combatant's initiative roll beyond the sheet - the auras they stand under (v8.368.8). */
  initiativeModFor?: (combatantId: string) => number;
  /**
   * Is this combatant hidden from the players? A hidden figure is never admitted, by any route: the shared
   * order would tell players it exists (ADR 2026-10-07). Omitted means nobody is.
   */
  isHidden?: (combatantId: string) => boolean;
  /** GM house rule: roll 1d10 + AB on admission. Default true. */
  autoRollInitiative?: boolean;
  /** GM house rule: does a mid-round arrival act in that round? */
  initiativeArrivals?: InitiativeArrival;
  /** GM house rule: what separates two equal initiatives. */
  initiativeTieBreak?: InitiativeTieBreak;
  /** GM house rule: everyone rerolls at the start of each round. Default false. */
  rerollEachRound?: boolean;
  /** GM house rule: a bulk reroll leaves hand-typed values alone. Default false. */
  rerollKeepsManual?: boolean;
  /** GM house rule: pass over the turns of the dead and unconscious. Default true. */
  skipDownedTurns?: boolean;
}

export interface UseTurnTrackerReturn {
  trackerState: TurnTrackerState;
  /** Roll a pending extinguish test. `assist` is the ally's Half Action bonus. */
  resolveTurnEffectTest: (req: TurnEffectTestRequest, assist: number) => void;
  toggleTracker: (active: boolean, keepState?: boolean) => void;
  endTurn: () => void;
  /** `beats` of time (one by default) for everyone, outside the rounds - one undoable step. See `runTimePasses`. */
  passTime: (beats?: number) => void;
  addTabParticipant: (combatantId: string, opts?: AdmitEntryOptions) => void;
  addTabParticipantBatch: (entries: TurnAdmission[]) => void;
  /** Throw the order away and rebuild it from these combatants. */
  replaceParticipants: (entries: TurnAdmission[]) => void;
  /**
   * Open a fight: everyone given joins, round 1, the surprised sit out. One undoable step (v8.430.0).
   * Returns the order it made, or null when the tracker is locked.
   */
  openFight: (
    entries: TurnAdmission[],
    opts: { reroll: boolean; sitsOut: readonly string[]; keep?: boolean },
  ) => TurnTrackerState | null;
  /** A stand-in takes the departed's slot - combatant ids, not participant ids (v8.343.0). */
  replaceParticipant: (outgoingCombatantId: string, incomingCombatantId: string, incomingName: string) => void;
  /**
   * Install an order made elsewhere, whole - a scene cut's (v8.337.0).
   * Not an edit: replaying it through the merge would reconcile two
   * unrelated orders. Takes no history snapshot - the cut clears history.
   */
  imposeTrackerState: (state: TurnTrackerState) => void;
  /** One participant for this person, initiative rolled if the rule says so - the cut's dice. */
  participantFor: (name: string, combatantId: string, sheet?: Character | null) => TurnParticipant;
  addCustomParticipant: (name: string) => void;
  removeParticipant: (participantId: string) => void;
  reorderParticipants: (fromIdx: number, toIdx: number) => void;
  setParticipantInitiative: (participantId: string, value: number | null) => void;
  /** Reroll one participant, always - the deliberate override of a typed value. */
  rerollInitiative: (participantId: string) => void;
  /** Reroll everyone, subject to `rerollKeepsManual`. */
  rerollAll: () => void;
  /** Reroll everyone EXCEPT the recent arrivals, who just rolled. */
  rerollOthers: () => void;
  /** Clear the "N joined" hint without rerolling anything. */
  dismissJoinHint: () => void;
  setOrderMode: (mode: TurnOrderMode) => void;
  togglePause: () => void;
  resetCombat: (keepParticipants: boolean) => void;
  isTabTracked: (combatantId: string) => boolean;
  /** Advisory: add to (or refund from) a combatant's spent points this round (v8.309.0). */
  spendActionPoints: (combatantId: string, points: number) => void;
  /** Zero them - the readout's manual override (v8.309.2). */
  resetActionPoints: (combatantId: string) => void;
  isTabActive: (combatantId: string) => boolean;
}

// ── Hook ─────────────────────────────────────────────────────

/**
 * The params with every house rule's default filled in. Destructuring
 * defaults, as the hook always had: only `undefined` takes the default.
 */
function withDefaults({
  autoResolveTurnEffects = false,
  roundCountdown = false,
  protagonistRules = false,
  unnaturalDosMode = DEFAULT_UNNATURAL_DOS_MODE,
  autoRollInitiative = true,
  initiativeArrivals = DEFAULT_INITIATIVE_ARRIVAL,
  initiativeTieBreak = DEFAULT_INITIATIVE_TIE_BREAK,
  rerollEachRound = false,
  rerollKeepsManual = false,
  skipDownedTurns = true,
  ...rest
}: UseTurnTrackerParams) {
  return {
    ...rest,
    autoResolveTurnEffects,
    roundCountdown,
    protagonistRules,
    unnaturalDosMode,
    autoRollInitiative,
    initiativeArrivals,
    initiativeTieBreak,
    rerollEachRound,
    rerollKeepsManual,
    skipDownedTurns,
  };
}

export type TrackerParams = ReturnType<typeof withDefaults>;
type Derive = (state: TurnTrackerState) => TurnTrackerState;

/**
 * The two ways a state reaches the server.
 *
 * `commitState` sends the EDIT, not the state: `turnTrackerState` here is
 * the copy every caller in this render derived its `next` from, so the
 * pair is exactly what the server needs to replay the change onto
 * whatever it holds by then (v8.310.6). A caller and this helper are
 * rebuilt in the same render, so they agree on the base.
 * `imposeTrackerState` REPLACES the document whole - undo and a scene cut.
 */
function useTrackerCommits(p: TrackerParams) {
  const { turnTrackerState, setTurnTrackerState, syncTurnTrackerChange, syncTurnTrackerToFirestore } = p;
  const commitState = useCallback(
    (state: TurnTrackerState) => {
      setTurnTrackerState(state);
      syncTurnTrackerChange(turnTrackerState, state);
    },
    [setTurnTrackerState, syncTurnTrackerChange, turnTrackerState],
  );
  const imposeTrackerState = useCallback(
    (state: TurnTrackerState) => {
      setTurnTrackerState(state);
      syncTurnTrackerToFirestore?.(state);
    },
    [setTurnTrackerState, syncTurnTrackerToFirestore],
  );
  return { commitState, imposeTrackerState };
}

/**
 * The shape of nearly every tracker action: refuse when locked, derive
 * the next state, then snapshot, commit and mark history - in that order.
 * `onlyIfChanged` makes a no-op edit take no snapshot and write nothing.
 */
function useUndoableEdit(p: TrackerParams, commitState: (state: TurnTrackerState) => void) {
  const { isLocked, turnTrackerState, capturePreActionSnapshot, historyTrack } = p;
  const commitUndoable = useCallback(
    (state: TurnTrackerState) => {
      capturePreActionSnapshot();
      commitState(state);
      historyTrack.current = true;
    },
    [capturePreActionSnapshot, commitState, historyTrack],
  );
  const edit = useCallback(
    (derive: Derive, onlyIfChanged = false) => {
      if (isLocked('turnTracker')) return;
      const next = derive(turnTrackerState);
      if (onlyIfChanged && next === turnTrackerState) return;
      commitUndoable(next);
    },
    [isLocked, turnTrackerState, commitUndoable],
  );
  return { commitUndoable, edit };
}

export type Commits = ReturnType<typeof useTrackerCommits> & ReturnType<typeof useUndoableEdit>;

/**
 * Whether a bulk reroll touches this participant. A hand-typed number is
 * a ruling the dice may not overturn, unless `force` - the per-chip
 * reroll doing it deliberately. `ids` null means everyone.
 */
function isRerollable(p: TurnParticipant, ids: readonly string[] | null, keepManual: boolean): boolean {
  if (ids && !ids.includes(p.id)) return false;
  return !(keepManual && p.initiativeManual);
}

/** Reroll a named set of participants. `null` means everyone eligible. The hint is answered either way. */
function useReroll(p: TrackerParams, { commitUndoable }: Commits) {
  const { isLocked, turnTrackerState, rerollKeepsManual, sheetFor, initiativeModFor, initiativeTieBreak } = p;
  return useCallback(
    (ids: readonly string[] | null, { force = false } = {}) => {
      if (isLocked('turnTracker')) return;
      const keepManual = !force && rerollKeepsManual;
      const targets = turnTrackerState.participants.filter((pt) => isRerollable(pt, ids, keepManual));
      if (targets.length === 0) return;
      const assignments = rollAssignments(targets, { sheetFor, initiativeModFor });
      commitUndoable(dismissJoinHintPure(applyInitiativePure(turnTrackerState, assignments, initiativeTieBreak)));
    },
    [turnTrackerState, isLocked, rerollKeepsManual, sheetFor, initiativeModFor, initiativeTieBreak, commitUndoable],
  );
}

/**
 * The initiative verbs. `rerollOthers` spares whoever just arrived: they
 * rolled on admission seconds ago, and `joined` - the set to exclude -
 * expires on the next round advance. Dismissing the hint takes no
 * snapshot: it is not an action anyone undoes.
 */
function useInitiativeActions(p: TrackerParams, commits: Commits) {
  const { isLocked, turnTrackerState } = p;
  const { commitState } = commits;
  const reroll = useReroll(p, commits);
  // Stable (plan shell-contexts, A2): `reroll` is rebuilt with the tracker's state, and the target context holding
  // this took a new value with every render. Clicked, never read while drawing.
  const rerollInitiative = useStableCallback((participantId: string) => reroll([participantId], { force: true }));
  const rerollAll = useCallback(() => reroll(null), [reroll]);
  const rerollOthers = useCallback(() => {
    const newcomers = new Set(turnTrackerState.joined);
    reroll(turnTrackerState.participants.filter((pt) => !newcomers.has(pt.id)).map((pt) => pt.id));
  }, [turnTrackerState, reroll]);
  const dismissJoinHint = useCallback(() => {
    if (isLocked('turnTracker')) return;
    const state = dismissJoinHintPure(turnTrackerState);
    if (state !== turnTrackerState) commitState(state);
  }, [turnTrackerState, isLocked, commitState]);
  return { rerollInitiative, rerollAll, rerollOthers, dismissJoinHint };
}

/**
 * The edits to the order itself, each one undoable step.
 *
 * DRAGGING IS THE MODE SWITCH: ordering by hand while the order derives
 * from initiative would put the chip back on the next sort, which reads
 * as the app fighting you. A typed initiative is COMMITTED, not
 * local-only - it decides the order, and the chip calls this on blur or
 * Enter, so one history entry per edit rather than per keystroke.
 */
function useOrderEdits({ initiativeTieBreak: tieBreak }: TrackerParams, { edit }: Commits) {
  const reorderParticipants = useCallback(
    (fromIdx: number, toIdx: number) =>
      edit((s) => reorderParticipantsPure(setOrderModePure(s, 'manual', tieBreak), fromIdx, toIdx)),
    [edit, tieBreak],
  );
  const setOrderMode = useCallback(
    (mode: TurnOrderMode) => edit((s) => setOrderModePure(s, mode, tieBreak), true),
    [edit, tieBreak],
  );
  const setParticipantInitiative = useCallback(
    (participantId: string, value: number | null) =>
      edit((s) => setInitiativePure(s, participantId, value, tieBreak), true),
    [edit, tieBreak],
  );
  return { reorderParticipants, setOrderMode, setParticipantInitiative };
}

/**
 * Who is in the order, and whether it runs. A stand-in takes the
 * departed's slot (v8.343.0) - combatant ids, not participant ids: the
 * caller is the roster verb and knows nothing of the `tab-` prefix.
 * Toggling writes no countdown RULE onto any combatant (v7.10.0): that
 * is one session-wide value. Switching OFF does clear the per-round tick
 * bookkeeping of whoever carries it (2026-10-04): the per-attack countdown
 * reads that flag first, so a flag left behind froze their conditions.
 */
function useMembershipEdits({ tabs, updateTab, isLocked }: TrackerParams, { edit }: Commits) {
  const toggleTracker = useCallback(
    (active: boolean, keepState?: boolean) => {
      // `edit` refuses a locked tracker by itself; the flags must not move without it.
      if (!active && !isLocked('turnTracker')) clearTickFlags({ tabs, updateTab });
      edit((s) => (active ? { ...s, active: true } : deactivateTrackerPure(s, keepState ?? false)));
    },
    [edit, tabs, updateTab, isLocked],
  );
  const removeParticipant = useCallback((id: string) => edit((s) => removeParticipantPure(s, id)), [edit]);
  const replaceParticipant = useCallback(
    (outgoing: string, incoming: string, incomingName: string) =>
      edit((s) => replaceParticipantPure(s, outgoing, incoming, incomingName), true),
    [edit],
  );
  const togglePause = useCallback(() => edit((s) => ({ ...s, paused: !s.paused })), [edit]);
  return { toggleTracker, removeParticipant, replaceParticipant, togglePause };
}

/**
 * Start the fight over. Keeping the participants resets round 1 with the
 * first active; otherwise the tracker is cleared. The countdown RULE is
 * the GM's and survives either way; only the per-round tick bookkeeping
 * is cleared, before the commit.
 */
function useResetCombat(p: TrackerParams, { commitState }: Commits) {
  const { tabs, isLocked, turnTrackerState, capturePreActionSnapshot, updateTab, historyTrack } = p;
  return useCallback(
    (keepParticipants: boolean) => {
      if (isLocked('turnTracker')) return;
      capturePreActionSnapshot();
      clearTickFlags({ tabs, updateTab });
      commitState(keepParticipants ? resetRoundPure(turnTrackerState) : deactivateTrackerPure(turnTrackerState, false));
      historyTrack.current = true;
    },
    [turnTrackerState, tabs, isLocked, capturePreActionSnapshot, updateTab, commitState, historyTrack],
  );
}

/**
 * Whose turn the scan passes over.
 *
 * DERIVED from conditions on every render and handed to `advanceTurn` as
 * an argument - never stored on the participant. A stored copy is a
 * second answer that can go out of step with the first, and reviving
 * somebody would then need a write to put them back in the order.
 */
function useDownedIds(tabs: Tab[], skipDownedTurns: boolean) {
  return useMemo(() => {
    if (!skipDownedTurns) return undefined;
    return new Set(tabs.filter((tab) => isDowned(tab.target?.conditions)).map((tab) => tab.id));
  }, [tabs, skipDownedTurns]);
}

/** Roll a pending extinguish test. `assist` is the ally's Half Action bonus. */
function useResolveTurnEffectTest({ tabs, unnaturalDosMode, addNarrative, updateTab }: TrackerParams) {
  return useCallback(
    (req: TurnEffectTestRequest, assist: number) =>
      resolveTurnEffectTestWith({ tabs, updateTab, addNarrative, unnaturalDosMode }, req, assist),
    [tabs, unnaturalDosMode, addNarrative, updateTab],
  );
}

/**
 * What one End Turn reads, as a factory rebuilt whenever any of it
 * changes. Every input is a dependency: End Turn once left out the aura
 * bonus, the protagonist rules and the round countdown, and read stale
 * values until the tracker or the tabs next changed (v8.377.2).
 */
function useTurnEndContext(p: TrackerParams, resolveTurnEffectTest: ReturnType<typeof useResolveTurnEffectTest>) {
  const { turnTrackerState, tabs, updateTab, addNarrative, roundCountdown, protagonistRules } = p;
  const { autoResolveTurnEffects, onTurnEffectTests, rerollEachRound, autoRollInitiative } = p;
  const { sheetFor, initiativeModFor, initiativeTieBreak, onTurnStart } = p;
  const downedIds = useDownedIds(tabs, p.skipDownedTurns);
  return useCallback(
    (): TurnEndContext => ({
      ...{ state: turnTrackerState, tabs, updateTab, addNarrative, roundCountdown, protagonistRules, downedIds },
      ...{ autoResolveTurnEffects, resolveTurnEffectTest, onTurnEffectTests, initiativeTieBreak },
      ...{ rerollEachRound: rerollEachRound && autoRollInitiative, sheetFor, initiativeModFor, onTurnStart },
    }),
    [
      turnTrackerState,
      tabs,
      updateTab,
      addNarrative,
      autoResolveTurnEffects,
      onTurnEffectTests,
      resolveTurnEffectTest,
      downedIds,
      rerollEachRound,
      autoRollInitiative,
      sheetFor,
      initiativeModFor,
      initiativeTieBreak,
      roundCountdown,
      protagonistRules,
      onTurnStart,
    ],
  );
}

/**
 * End Turn: one snapshot, every write of the turn change (see
 * turnTrackerTurn for their order), one commit - so one Ctrl+Z reverts
 * the turn, its countdowns, its burn and any reroll together.
 */
function useEndTurn(p: TrackerParams, { commitState }: Commits, turnEndContext: () => TurnEndContext) {
  const { turnTrackerState, isLocked, capturePreActionSnapshot, historyTrack } = p;
  return useCallback(() => {
    if (isLocked('turnTracker') || turnTrackerState.participants.length === 0) return;
    capturePreActionSnapshot();
    commitState(runEndTurn(turnEndContext()));
    historyTrack.current = true;
  }, [turnTrackerState, isLocked, capturePreActionSnapshot, commitState, historyTrack, turnEndContext]);
}

/**
 * Time passes: one snapshot, then every combatant's beats (see
 * `runTimePasses` for the order) - so one Ctrl+Z takes it all back. It touches no turn and no round, and is not the tracker's to lock:
 * whoever directs is the only one offered it.
 */
function usePassTime(p: TrackerParams) {
  const { tabs, updateTab, addNarrative, protagonistRules, autoResolveTurnEffects, onTurnEffectTests } = p;
  const { capturePreActionSnapshot, historyTrack, unnaturalDosMode, onTurnStart } = p;
  return useCallback(
    (beats = 1) => {
      if (!(beats >= 1)) return;
      capturePreActionSnapshot();
      runTimePasses(
        {
          ...{ tabs, updateTab, addNarrative, protagonistRules },
          ...{ autoResolveTurnEffects, unnaturalDosMode, onTurnEffectTests, onTurnStart },
        },
        Math.floor(beats),
      );
      historyTrack.current = true;
    },
    [
      tabs,
      updateTab,
      addNarrative,
      protagonistRules,
      autoResolveTurnEffects,
      onTurnEffectTests,
      unnaturalDosMode,
      capturePreActionSnapshot,
      historyTrack,
      onTurnStart,
    ],
  );
}

/** Set one tracked combatant's spent points; null when there is nothing to change. Clamped at zero (v8.309.2). */
function withActionPoints(
  state: TurnTrackerState,
  combatantId: string,
  next: (current: number) => number,
): TurnTrackerState | null {
  if (!state?.active || !combatantId) return null;
  const idx = state.participants.findIndex((p) => p.combatantId === combatantId);
  if (idx < 0) return null;
  const current = state.participants[idx].actionPoints ?? 0;
  const value = Math.max(0, next(current));
  if (value === current) return null;
  return { ...state, participants: state.participants.map((p, i) => (i === idx ? { ...p, actionPoints: value } : p)) };
}

/**
 * Charge a combatant's turn (v8.309.0). ADVISORY: it only counts, and
 * nothing anywhere reads it to refuse an action. Silently does nothing
 * when the tracker is off or the combatant is not in the order - there
 * is no turn to spend from. Not undoable, and not locked.
 */
function useActionPoints({ turnTrackerState }: TrackerParams, { commitState }: Commits) {
  const setActionPoints = useCallback(
    (combatantId: string, next: (current: number) => number) => {
      const state = withActionPoints(turnTrackerState, combatantId, next);
      if (state) commitState(state);
    },
    [turnTrackerState, commitState],
  );
  const spendActionPoints = useCallback(
    (combatantId: string, points: number) => {
      if (points) setActionPoints(combatantId, (current) => current + points);
    },
    [setActionPoints],
  );
  const resetActionPoints = useCallback(
    (combatantId: string) => setActionPoints(combatantId, () => 0),
    [setActionPoints],
  );
  return { spendActionPoints, resetActionPoints };
}

/**
 * Queries. There is deliberately no name sync effect: participant names
 * are resolved from tabs at render time in TurnTrackerBar, which avoids
 * extra renders and divergence from the Firestore history top.
 */
function useTrackerQueries({ turnTrackerState }: TrackerParams) {
  const isTabTracked = useCallback(
    (combatantId: string) => isTrackedParticipantPure(turnTrackerState, combatantId),
    [turnTrackerState],
  );
  const isTabActive = useCallback(
    (combatantId: string) => isActiveParticipantPure(turnTrackerState, combatantId),
    [turnTrackerState],
  );
  return { isTabTracked, isTabActive };
}

/**
 * The turn tracker: the order, the turn, initiative, and every edit to
 * them - each one a small hook above, composed here. See the file header
 * and the params/return types for the contract.
 */
/**
 * A HIDDEN FIGURE IN THE ORDER IS TAKEN OUT BY THE GM'S SCREEN (2026-10-17). Every door refuses one, but a player's
 * door cannot know who is hidden: a "Track Participant" sent before they heard of the hide put the figure in the
 * shared order, in front of every player (the night fuzz's hidden-leak). Only the GM's screen knows (`isHidden`), so it
 * repairs, as it deletes a stray combatant document (useHiddenFigures `reportStrays`). A repair, not an edit: no undo
 * snapshot. The hide keeps the initiative before the figure counts as hidden, so nothing is lost here.
 */
function useHiddenOutOfOrder(p: TrackerParams, commitState: (state: TurnTrackerState) => void) {
  const { turnTrackerState, isHidden, sheetFor, isGm } = p;
  useEffect(() => {
    if (!isHidden) return;
    // And an OBJECT (2026-10-26): a figure re-bodied as one mid-fight leaves the order the same way - a thing takes
    // no turn. Re-bodied back, it is an ordinary arrival again. The GM's screen alone: every screen holds sheets,
    // and every client writing the same removal at once is a race (the review).
    const out = (id: string) => isHidden(id) || (!!isGm && isObject(sheetFor?.(id)));
    const strays = turnTrackerState.participants.filter((t) => t.combatantId && out(t.combatantId));
    if (strays.length) commitState(strays.reduce((s, t) => removeParticipantPure(s, t.id), turnTrackerState));
  }, [turnTrackerState, isHidden, sheetFor, isGm, commitState]);
}

export default function useTurnTracker(params: UseTurnTrackerParams): UseTurnTrackerReturn {
  const p = withDefaults(params);
  const { commitState, imposeTrackerState } = useTrackerCommits(p);
  useHiddenOutOfOrder(p, commitState);
  const commits = { commitState, imposeTrackerState, ...useUndoableEdit(p, commitState) };
  const { build, ...admission } = useAdmission(p, commits);
  const resolveTurnEffectTest = useResolveTurnEffectTest(p);
  return {
    trackerState: p.turnTrackerState,
    ...useActionPoints(p, commits),
    resolveTurnEffectTest,
    endTurn: useEndTurn(p, commits, useTurnEndContext(p, resolveTurnEffectTest)),
    passTime: usePassTime(p),
    ...admission,
    imposeTrackerState,
    participantFor: (name, combatantId, sheet) => build({ name, combatantId, isCustom: false, sheet }),
    ...useMembershipEdits(p, commits),
    ...useOrderEdits(p, commits),
    ...useInitiativeActions(p, commits),
    resetCombat: useResetCombat(p, commits),
    ...useTrackerQueries(p),
  };
}
